//#region imports
import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { inviteEmployee, selectInviteStatus, selectInviteError } from '_redux/slices/profile/staff-management';
//#endregion

const ROLE_OPTIONS = [
    { value: 'REPORTS_ONLY', label: 'Reports' },
    { value: 'SECURITY', label: 'Security' },
    { value: 'ALLOCATOR', label: 'Allocator' },
    { value: 'DESK_HOST', label: 'Desk Host' },
    { value: 'OPERATIONS_ADMINISTRATOR', label: 'Operations Administrator' },
];

const InviteMemberModal = ({ show, onHide, onInvited }) => {

    const dispatch = useDispatch();

    const inviteStatus = useSelector(selectInviteStatus);
    const inviteError = useSelector(selectInviteError);

    const [formData, setFormData] = useState({ name: '', email: '', role: 'REPORTS_ONLY' });
    const [validationErrors, setValidationErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            const result = await dispatch(inviteEmployee({
                name: formData.name.trim(),
                email: formData.email.trim(),
                role: formData.role,
            })).unwrap();

            setFormData({ name: '', email: '', role: 'REPORTS_ONLY' });
            setValidationErrors({});
            if (onInvited) onInvited(result?.message);
            onHide();
        } catch (error) {
            // error surfaced via inviteError selector
        }
    };

    const handleClose = () => {
        setFormData({ name: '', email: '', role: 'REPORTS_ONLY' });
        setValidationErrors({});
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Invite Team Member</Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <p className="text-muted small">
                        The invitee will receive an email with a temporary password and will be
                        asked to verify their email, change their password and set up
                        multi-factor authentication on first login.
                    </p>

                    {inviteStatus === 'failed' && inviteError && (
                        <Alert variant="danger">{inviteError}</Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            placeholder="Enter full name"
                            value={formData.name}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.name}
                            disabled={inviteStatus === 'loading'}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            placeholder="Enter email address"
                            value={formData.email}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.email}
                            disabled={inviteStatus === 'loading'}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.email}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Role</Form.Label>
                        <Form.Select
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                            disabled={inviteStatus === 'loading'}
                        >
                            {ROLE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={inviteStatus === 'loading'}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={inviteStatus === 'loading'}>
                        {inviteStatus === 'loading' ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Sending...
                            </>
                        ) : 'Send Invite'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default InviteMemberModal;
